import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Calendar, MapPin, Building, GraduationCap } from 'lucide-react';
import LightRays from './LightRays';

// Import des images
import muntadacom from '../images/muntadacom.jpg';
import letdem from '../images/LETDEM.jpg';
import fsac from '../images/FSAC.webp';
import allformations from '../images/allformations.jpeg';
import estem from '../images/ESTEM.webp';
import gso from '../images/GSO.png';
import ofppt from '../images/ofppt.png';

const Experience = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const experiences = [
    {
      id: 1,
      type: 'stage',
      title: 'Stage de fin d\'études',
      company: 'MuntadaCom - Compagnie Marocaine Spécialisée en Digitalisation des Processus',
      location: 'Casablanca',
      period: 'Avril 2025 – Juillet 2025',
      description: 'Développement d\'une application web de gestion RH avec React.js pour le frontend, Node.js et Express.js pour le backend, et MySQL pour la base de données, permettant de gérer les employés, congés et notes de frais de manière efficace.',
      technologies: ['React.js', 'Node.js', 'Express.js', 'MySQL'],
      image: muntadacom
    },
    {
      id: 2,
      type: 'stage',
      title: 'Stage',
      company: 'Laboratoire d\'études transversales en droit, Économie et Management des affaires',
      location: 'Casablanca',
      period: 'Décembre 2024 - Mars 2025',
      description: 'Conception et développement d\'une application web destinée à présenter les services et activités d\'un laboratoire. Réalisée avec React.js pour le frontend et Laravel pour le backend afin de garantir une gestion sécurisée et performante des données.',
      technologies: ['React.js', 'Laravel', 'PHP'],
      images: [letdem, fsac] // Deux images pour ce stage
    },
    {
      id: 3,
      type: 'stage',
      title: 'Stage de fin d\'études',
      company: 'All Formations',
      location: 'France',
      period: 'Février 2024 – Mai 2024',
      description: 'Développement d\'une application web fullstack pour l\'adoption d\'animaux avec ReactJS, TailwindCSS, Laravel et MySQL. Interface utilisateur moderne, backend robuste et gestion de projet avec Git.',
      technologies: ['ReactJS', 'TailwindCSS', 'Laravel', 'MySQL', 'Git'],
      image: allformations
    }
  ];

  const education = [
    {
      id: 1,
      degree: 'Licence professionnelle Génie Logiciel',
      school: 'ESTEM',
      location: 'Casablanca',
      period: '2024-2025',
      description: 'Formation spécialisée en génie logiciel couvrant les méthodologies de développement, architectures logicielles et technologies avancées.',
      image: estem
    },
    {
      id: 2,
      degree: 'Technicien en développement informatique',
      school: 'OFPPT centre de formation professionnelle',
      location: 'Casablanca',
      period: '2022-2024',
      description: 'Formation complète en développement informatique couvrant les technologies web, bases de données et méthodologies de développement.',
      image: ofppt
    },
    {
      id: 3,
      degree: 'Baccalauréat en science physique',
      school: 'Groupe scolaire Ouhoud',
      location: 'Casablanca',
      period: '2020-2021',
      description: 'Formation scientifique de base en sciences physiques.',
      image: gso
    }
  ];

  const ExperienceCard = ({ item, index, type }) => (
    <motion.div
      initial={{ opacity: 0, x: type === 'experience' ? -50 : 50 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className={`relative p-6 bg-white dark:bg-dark-700 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 ${
        type === 'experience' ? 'border-l-4 border-primary-500' : 'border-l-4 border-secondary-500'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          {/* Affichage des images */}
          {(item.image || item.images) && (
            <div className="flex items-center space-x-2">
              {item.images ? (
                // Affichage de plusieurs images (pour le stage LETDEM)
                item.images.map((img, imgIndex) => (
                  <div key={imgIndex} className="w-12 h-12 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-600">
                    <img 
                      src={img} 
                      alt={`${type === 'experience' ? item.company : item.school} ${imgIndex + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))
              ) : (
                // Affichage d'une seule image
                <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-gray-200 dark:border-gray-600">
                  <img 
                    src={item.image} 
                    alt={type === 'experience' ? item.company : item.school}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          )}
          
          <div className={`p-2 rounded-lg ${
            type === 'experience' 
              ? 'bg-primary-100 dark:bg-primary-900' 
              : 'bg-secondary-100 dark:bg-secondary-900'
          }`}>
            {type === 'experience' ? (
              <Building size={20} className="text-primary-600 dark:text-primary-400" />
            ) : (
              <GraduationCap size={20} className="text-secondary-600 dark:text-secondary-400" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {type === 'experience' ? item.title : item.degree}
            </h3>
            <p className="text-primary-600 dark:text-primary-400 font-medium">
              {type === 'experience' ? item.company : item.school}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-1">
            <Calendar size={14} className="mr-1" />
            {item.period}
          </div>
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <MapPin size={14} className="mr-1" />
            {item.location}
          </div>
        </div>
      </div>

      <p className="text-gray-600 dark:text-gray-400 mb-4">
        {item.description}
      </p>

      {type === 'experience' && item.technologies && (
        <div className="flex flex-wrap gap-2">
          {item.technologies.map((tech, techIndex) => (
            <span
              key={techIndex}
              className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-medium"
            >
              {tech}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );

  return (
    <section id="experience" className="section-padding bg-gray-50 dark:bg-dark-900 relative overflow-hidden">
      {/* Animation LightRays en arrière-plan */}
      <div style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
        <LightRays
          raysOrigin="top-center"
          raysColor="#00ffff"
          raysSpeed={1.5}
          lightSpread={0.8}
          rayLength={1.2}
          followMouse={true}
          mouseInfluence={0.1}
          noiseAmount={0.1}
          distortion={0.05}
          className="custom-rays"
        />
      </div>
      
      <div className="container-custom relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Expérience <span className="gradient-text">& Formation</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Mon parcours professionnel et académique, illustrant mon évolution dans le domaine du développement informatique.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Expériences professionnelles */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 flex items-center">
              <Building size={28} className="mr-3 text-primary-600 dark:text-primary-400" />
              Expériences professionnelles
            </h3>
            <div className="space-y-6">
              {experiences.map((experience, index) => (
                <ExperienceCard
                  key={experience.id}
                  item={experience}
                  index={index}
                  type="experience"
                />
              ))}
            </div>
          </div>

          {/* Formation */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 flex items-center">
              <GraduationCap size={28} className="mr-3 text-secondary-600 dark:text-secondary-400" />
              Formation
            </h3>
            <div className="space-y-6">
              {education.map((edu, index) => (
                <ExperienceCard
                  key={edu.id}
                  item={edu}
                  index={index}
                  type="education"
                />
              ))}
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Envie de découvrir plus sur mon parcours ou de discuter d'opportunités ?
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn-primary"
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
          >
            Me contacter
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience; 